import React from "react";
import { Button } from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";
import Milestone from "./Milestone";
import { formatMoney, monthsLabel, planTermLabel } from "../pricing";
import type { TermChangeView } from "../termChange";
import type {
  AppliedCoupon,
  PriceBreakdown,
  SubscriptionPlan,
} from "../types/billing.types";

/**
 * What a coupon took off this order — and only a coupon.
 *
 * The quote's `discount` is not always one. A plan on a launch offer is quoted
 * at its list price with the offer expressed as a discount beneath it, so
 * reading the figure alone put a "Discount — ₹200" line on an order nobody had
 * applied anything to, and a "You saved ₹200" for a price that is simply what
 * the plan costs. The saving against the list price is the duration row's
 * business; this card is about what is being charged.
 *
 * So the *coupon* is what is asked, not the figure. Exported because the
 * checkout draws the "You saved" line itself on a phone, where the pay action
 * is at the foot of the page rather than inside this card — and the two have
 * to agree about when there is a saving at all.
 */
export const couponSaving = (
  coupon: AppliedCoupon | null,
  price: PriceBreakdown
): number => (coupon && price.discount > 0 ? price.discount : 0);

/**
 * What this purchase is saving, in the words it is saved by — one line per
 * reason, in the order they are taken off.
 *
 * Two reasons, and they are not the same claim. A coupon is a discount
 * somebody applied; an upgrade credit is their own unused days coming back to
 * them, and rolling the two into a single "You saved" would tell a practice
 * that a code they typed was worth three thousand rupees.
 *
 * Exported because the checkout draws these above its own Pay button on a
 * phone, where the action is at the foot of the screen rather than inside this
 * card — and the two have to agree about what is being claimed.
 */
export interface PurchaseSaving {
  key: string;
  text: string;
}

export const purchaseSavings = (
  coupon: AppliedCoupon | null,
  price: PriceBreakdown,
  change: TermChangeView | null = null
): PurchaseSaving[] => {
  const savings: PurchaseSaving[] = [];
  const byCoupon = couponSaving(coupon, price);

  if (byCoupon > 0) {
    savings.push({ key: "coupon", text: `You saved ${formatMoney(byCoupon)}` });
  }
  if (change && change.credit > 0) {
    savings.push({
      key: "credit",
      text: `You save ${formatMoney(change.credit)} with your upgrade credit`,
    });
  }

  return savings;
};

/** An ISO day as the cards write one: `"2027-08-20"` reads "20 Aug, 2027".
 *  Dated at local midnight rather than parsed as UTC, which is what stops a
 *  day drifting backwards for a reader west of Greenwich. */
const day = (iso: string): string =>
  formatCreatedOn(new Date(`${iso}T00:00:00`));

/** One line of the breakdown: what it is on the left, what it comes to on the
 *  right. Tabular figures so the column of rupees lines up. */
const Line: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  /** A line that is money coming off rather than going on. */
  credit?: boolean;
}> = ({ label, value, credit = false }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span
      className={`text-[13px] 3xl:text-[14px] ${
        credit ? "text-[#00A86B]" : "text-ink-muted"
      }`}
    >
      {label}
    </span>
    <span
      className={`tabular-nums text-[13px] 3xl:text-[14px] font-medium ${
        credit ? "text-[#00A86B]" : "text-ink"
      }`}
    >
      {value}
    </span>
  </div>
);

/**
 * The badge the plan's own offer wears on the line that takes it off.
 *
 * Violet, which is the colour this feature gives every aside beside a figure —
 * the coupon tag on the milestone below and the "You saved" line under the
 * button are the same ink. It is a name rather than a figure, so it is not set
 * in the tabular column the rupees line up in.
 *
 * The words are fixed because nothing on the wire names the offer. The price
 * list answers what a term is listed at and what it is sold at, and a quote
 * answers what came off with no code behind it; the difference is all there
 * is, and neither carries a name for it. Naming this line from a field that
 * does not exist is the one thing it must not do — if the offers are ever to
 * be named on screen, that is a field to ask the backend for.
 */
const OFFER_LABEL = "Welcome offer";

/** Money to the paisa — the same rounding `pricing` does, so a figure worked
 *  out here cannot land a hundredth away from the ones it is set beside. */
const round2 = (amount: number): number => Math.round(amount * 100) / 100;

interface OrderSummaryProps {
  plan: SubscriptionPlan;
  price: PriceBreakdown;
  /** The coupon the last quote came back carrying, or `null`. */
  coupon?: AppliedCoupon | null;
  onPay: () => void;
  /** Back to the plans, to buy a different term. Left off where there is
   *  nowhere to go back to. */
  onEdit?: () => void;
  /**
   * Opens the list of coupons on offer — what the "Apply" on the coupon line
   * presses.
   *
   * With no coupon on the order that line has no figure, so it carries the way
   * to find one instead: the discount is named where it would land, and the
   * thing to press to make it land is on the same row. Without this the line
   * is simply not drawn, which is what an order with no coupon and nowhere to
   * browse looked like before.
   */
  onBrowseCoupons?: () => void;
  /** The purchase is with the server — the button spins and stays pressed. */
  paying?: boolean;
  /** A re-quote is in flight, so the figures are about to be replaced: the
   *  button stands down rather than taking a payment against a total that is
   *  being recalculated. */
  pricing?: boolean;
  /**
   * Whether the card carries the button that moves the checkout on.
   *
   * False past the first step: from then on the thing to press belongs to the
   * step being shown — a method to choose, a mandate to send — and a second
   * button over here saying "Pay now" would be a race between two ways
   * forward. The figures stay up throughout, which is the point of the card.
   *
   * The "You saved" line goes with it. It is not one of the figures — the
   * breakdown above is what the total has to be checkable against — it is the
   * thing somebody reads just before pressing, so it belongs wherever the
   * button ended up.
   */
  payable?: boolean;
  /**
   * How the card presents itself.
   *
   * `card` is the summary that stands beside the form on a wide screen: white,
   * headed "Order summary", with the figures it is named for.
   *
   * `panel` is the phone's, and it is a different thing on the page rather
   * than the same thing narrower. There it is not a card *beside* anything —
   * it is the first thing under the coupon row and the whole of what is being
   * confirmed, so it is headed "Plan details" the way the design names it,
   * tinted to set it apart from the three white form cards below, and the
   * discount line drops the code it is wearing in brackets: the milestone
   * under it already names the coupon, and the two set against a price on a
   * 360px row is a line that wraps.
   */
  variant?: "card" | "panel";
  /**
   * The term this purchase replaces or queues behind, where there is one.
   *
   * Set on an `UPGRADE` and on a `DOWNGRADE` — see `termChangeView`, which
   * reads it off the quote the figures beside it already came from. An upgrade
   * adds the credit line and the saving claimed from it; a downgrade adds the
   * handover rows and turns the second stop into the day the new term begins
   * rather than the day it ends. `null` on every other purchase, which is
   * nearly all of them.
   */
  change?: TermChangeView | null;
}

/**
 * What is being bought and what it comes to, beside the form that says who it
 * is billed to.
 *
 * Every figure comes off `POST /billing/quote/` — see `quoteService` — and the
 * lines are shown even where they could be inferred: a total on its own is a
 * number to be taken on trust, and the subtotal, the discount and the tax are
 * what make it one that can be checked.
 *
 * ## It works downwards from the list price
 *
 * The card opens on what the term is *listed* at and takes things off it, in
 * the order the server took them off: the plan's own offer, then the subtotal
 * it leaves, then a coupon, then an upgrade credit, then the tax on what is
 * left. Every one of them adds up on screen.
 *
 * It used to open on the net figure instead — the term at whatever price it
 * was actually being sold at, with nothing to say what it had been marked down
 * from. That is a defensible card and a worse one: a launch offer is the
 * single best thing this screen has to say, and the old arrangement was the
 * one place in the product where it was invisible. The duration row a step
 * earlier strikes the list price through beside the sale one; the summary then
 * quoted the sale price alone, so the saving appeared to evaporate between the
 * two screens.
 *
 * The coupon line keeps its place in that column whether or not there is a
 * coupon. With one, it is the figure that came off, named by its code. With
 * none, it is the name of a discount and the way to go and find one — see
 * `onBrowseCoupons`. That is the one line here that is not purely arithmetic,
 * and it earns the exception: it is the only thing on the card a reader can
 * change.
 *
 * The two dates under the total are the other half of what is being agreed to:
 * what happens now, and what happens at the end of the term. They are stops on
 * one line because that is what they are — the same subscription, at two points
 * in its life.
 *
 * Two purchases are about two terms rather than one, and they are not the same
 * story. Buying a *longer* plan while one is running ends the old term today
 * and credits whatever was paid for and not used, so the card grows a credit
 * line, the first stop names the term being moved to rather than access that
 * was unlocked weeks ago, and the saving under the button is claimed as a
 * credit rather than as a discount. Buying a *shorter* one changes nothing
 * today: the term running now is untouched, the new one is queued behind it,
 * and the card says when each of them stands rather than what came off.
 *
 * All of it off the `change` prop, which is the server's own answer — see
 * `termChangeView`.
 */
const OrderSummary: React.FC<OrderSummaryProps> = ({
  plan,
  price,
  coupon = null,
  onPay,
  onEdit,
  onBrowseCoupons,
  paying = false,
  pricing = false,
  payable = true,
  variant = "card",
  change = null,
}) => {
  const panel = variant === "panel";
  // No code on the order, no figure on the discount line — it then carries the
  // way to find one instead; see `onBrowseCoupons`.
  const saved = couponSaving(coupon, price);
  // What the replaced term's unused days took off, where a term is being cut
  // short - always 0 on a downgrade, which cuts nothing short.
  // Read off the change rather than off the breakdown, because the
  // breakdown's `credit` is not on `PriceBreakdown` at all — see
  // `prepaid.types`, which says why that shape was not widened for it.
  const credit = change?.credit ?? 0;
  const savings = purchaseSavings(coupon, price, change);

  /* ── Working the card downwards from the list price ────────────────────
     The server quotes a base, a discount and what the tax was charged on, and
     a launch offer can reach this card either of two ways: baked into the base
     — the price list quoting ₹1,800 for a term listed at ₹2,400 — or left on
     the quote as a discount with no code behind it, the base then being the
     list price itself. Both are real answers, and the card has to open on the
     same ₹2,400 either way or the saving a reader was shown a screen earlier
     would appear to evaporate.

     So the three figures are worked out rather than read straight off:        */

  // A discount with no coupon behind it is the plan's own offer. That is the
  // whole of what distinguishes the two — `couponSaving` above is the same
  // judgement from the other side, and the two are deliberately each other's
  // complement so no figure is ever claimed twice.
  const offerOnQuote = coupon ? 0 : price.discount;
  // What the supply is actually sold at, before a coupon or a credit: the base
  // less whatever of it was an offer. This is the subtotal line.
  const soldPrice = round2(price.subtotal - offerOnQuote);
  /* What the term is listed at — the figure the card opens on. The price
     list's own `list_amount` where it is higher, which is the figure that was
     actually listed to the paisa, and otherwise the base, which is where a
     quote carrying its offer as a discount keeps it. Never below the base, so
     the offer line underneath cannot come out negative. */
  const listPrice = Math.max(plan.listCyclePrice, price.subtotal);
  // The offer itself: what the list price was marked down by. 0 on a term sold
  // at the price it is listed at, and the line is then not drawn.
  const offer = round2(listPrice - soldPrice);
  // The discount line is drawn with a figure on it, or with the way to go and
  // get one. An order with neither — a card that cannot browse and has no
  // coupon — draws no line at all, which is what it did before.
  const showCouponLine = saved > 0 || !!onBrowseCoupons;

  return (
    <section
      aria-labelledby="order-summary-heading"
      className={`card-surface border border-[#5BD3E1] p-5 3xl:p-6 ${
        panel ? "bg-[#F6FDFE]" : "bg-white"
      }`}
    >
      <h2
        id="order-summary-heading"
        className={
          panel
            ? "text-[13px] font-medium uppercase tracking-[0.04em] text-[#00424B]"
            : "text-[16px] 3xl:text-[18px] font-semibold text-ink"
        }
      >
        {panel ? "Plan details" : "Order summary"}
      </h2>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[12px] 3xl:text-[13px] text-ink-muted">
            {plan.productName}
          </p>
          <p className="mt-0.5 flex flex-wrap items-baseline gap-2 text-[15px] 3xl:text-[16px] font-semibold text-ink">
            {planTermLabel(plan.months)}
            {onEdit && (
              // Changing the term is changing what is being bought, so it goes
              // back to the list rather than re-pricing in place — the plans
              // screen is where that choice is made, and making it twice in two
              // controls is how the two would quietly disagree.
              <button
                type="button"
                onClick={onEdit}
                disabled={paying}
                className="text-[13px] 3xl:text-[14px] font-medium text-[#00BACB] underline-offset-2 transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                Edit
              </button>
            )}
          </p>
        </div>
        {/* The term at the price it is listed at, which is the figure the
            whole card now works down from. Everything that comes off it — the
            plan's own offer, a coupon, an upgrade credit — is a line beneath
            it, so a reader can see what the full price was and what each thing
            took off it, rather than being handed a net figure and asked to
            take it on trust. See `listPrice`. */}
        <span className="tabular-nums text-[15px] 3xl:text-[16px] font-medium text-ink">
          {formatMoney(listPrice)}
        </span>
      </div>

      {offer > 0 && (
        // The plan's own offer, taken off the list price directly under it.
        // It is the first thing off because it is the first thing the server
        // applies: what a coupon and a credit come off is the offer price, not
        // the list one. The lines on this card add up on screen, and they can
        // only do that in the order the arithmetic was actually done.
        <div className="mt-3">
          <Line
            credit
            label={
              <span className="inline-flex rounded-full border border-[#D9CCFA] bg-[#F7F3FF] px-2 py-0.5 text-[10px] 3xl:text-[11px] font-semibold uppercase tracking-wide text-[#6234CC]">
                {OFFER_LABEL}
              </span>
            }
            value={`- ${formatMoney(offer)}`}
          />
        </div>
      )}

      {change?.queued && (
        // The handover, on the one purchase that does not start today. A
        // downgrade is queued behind the term already running: that term is
        // untouched and runs to its own last day, and what is being bought
        // here begins the morning after.
        //
        // It is drawn as two plain rows rather than as money coming off,
        // because neither of them is a figure in the arithmetic below — they
        // are *when*, which is the only thing about this purchase that is not
        // the same as any other. See `termChangeView`.
        <div className="mt-4 flex flex-col gap-2.5 border-t border-line pt-4">
          {change.currentEndsOn && (
            <Line
              label={`Current plan until ${day(change.currentEndsOn)}`}
              value={
                change.fromMonths === null
                  ? "Your plan"
                  : monthsLabel(change.fromMonths)
              }
            />
          )}
          <Line
            label={`New plan from ${day(change.startsOn)}`}
            value={monthsLabel(change.toMonths)}
          />
        </div>
      )}

      {/* What the supply is actually sold at, and then what comes off it.
          Subtotal is the sold price rather than the taxable amount this line
          used to print: the coupon and the credit are now drawn *under* it
          rather than over it, so this is the figure they are visibly taken
          from, and the subtotal less the two of them plus the tax is the total
          beneath. See `PrepaidPrice`, which is the order the server applies
          them in. */}
      <div className="mt-4 flex flex-col gap-2.5 border-t border-line pt-4">
        <Line label="Subtotal" value={formatMoney(soldPrice)} />

        {showCouponLine && (
          <Line
            credit
            // Named by its code, because with a figure beside it that is the
            // only thing this line can be: a coupon somebody applied. Not on
            // the phone's panel, where the milestone below already carries the
            // code and the two together would wrap the line — see `variant`.
            // And not at all with no coupon on the order, where the line is
            // the name of a discount that has not been found yet.
            label={
              saved > 0 && !panel
                ? `Coupon discount (${coupon?.code})`
                : "Coupon discount"
            }
            value={
              saved > 0 ? (
                `- ${formatMoney(saved)}`
              ) : (
                /* No figure, so the way to go and get one. It opens the list
                   of coupons this workspace has been given rather than
                   focusing the code box above: somebody who had a code would
                   have typed it, and the question this line raises is "is
                   there one for me" — which only the list can answer. A code
                   can still be typed into the box, and applying it from
                   either place goes through the same quote. */
                <button
                  type="button"
                  onClick={onBrowseCoupons}
                  disabled={paying || pricing}
                  className="font-medium text-[#00BACB] underline-offset-2 transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Apply
                </button>
              )
            }
          />
        )}

        {credit > 0 && (
          // Under the coupon rather than over it, because that is the order
          // the server applies them in: the code comes off the sold price and
          // the unused days come off what is left.
          <Line
            credit
            label="Credit for unused time"
            value={`- ${formatMoney(credit)}`}
          />
        )}

        <Line
          label={`GST ${Math.round(price.taxRate * 100)}%`}
          value={formatMoney(price.tax)}
        />
      </div>

      <div className="mt-4 flex items-baseline justify-between gap-4 border-t border-line pt-4">
        <span className="text-[16px] 3xl:text-[18px] font-semibold text-ink">
          Final amount
        </span>
        <span className="tabular-nums text-[21px] 3xl:text-[24px] font-bold text-ink">
          {formatMoney(price.total)}
        </span>
      </div>

      <ul className="mt-5">
        <Milestone
          done
          /* Three stories over the same two stops, because today means
             something different in each.

             A first purchase unlocks the product. An upgrade does not — the
             reader has had the access for weeks, and what is new today is
             which term they are on. And a downgrade changes nothing at all
             today, which is the single most important thing this screen can
             say to somebody about to pay for one: the term they are on is not
             being cut short and they are not losing anything this afternoon.
             See `termChangeView`. */
          title={
            !change
              ? "Today full access unlocked"
              : change.queued
                ? "Now - no change"
                : `Today your ${change.toMonths}-month plan will be active`
          }
          detail={
            !change
              ? `Get complete access to ${plan.productName}.`
              : change.queued
                ? `You keep ${
                    change.fromMonths === null
                      ? "your current plan"
                      : monthsLabel(change.fromMonths)
                  } and full ${plan.productName} access until it ends.`
                : `Full access to everything in ${plan.productName}, effective now.`
          }
          tag={
            coupon ? (
              <span className="rounded-full border border-[#D9CCFA] bg-[#F7F3FF] px-2 py-0.5 text-[10px] 3xl:text-[11px] font-semibold tracking-wide text-[#6234CC]">
                {coupon.code} applied
              </span>
            ) : undefined
          }
        />
        <Milestone
          last
          /* The second stop is the end of the term on every purchase but the
             queued one, where it is the day the term being bought *starts* —
             that is the thing a reader is agreeing to, and the day it then
             ends is said in the line beneath rather than given a stop of its
             own. Two stops either way: a third would make a timeline of what
             is, in the end, one handover. */
          title={
            change?.queued
              ? `${day(change.startsOn)} - your ${monthsLabel(
                  change.toMonths
                )} plan starts`
              : `${formatCreatedOn(price.renewsOn)}, plan ends`
          }
          detail={
            change?.queued
              ? `Paid for today, so nothing is charged again when it starts. It runs to ${formatCreatedOn(
                  price.renewsOn
                )}.`
              : "Nothing renews on its own. We'll email you 7 days before it ends, so there are no surprises."
          }
        />
      </ul>

      {payable &&
        // Under the timeline rather than beside the lines they come off: those
        // are arithmetic the total has to be checkable against, and these are
        // the single thing somebody wants to carry away from it — which is why
        // they travel with the button rather than staying here when the button
        // goes. A screen that moves Pay to the foot of the page draws them
        // directly above it; see `purchaseSavings`.
        savings.map((saving) => (
          <p
            key={saving.key}
            className="mt-4 text-center text-[13px] 3xl:text-[14px] font-medium text-[#6234CC]"
          >
            {saving.text}
          </p>
        ))}

      {payable && (
        <Button
          variant="primary"
          size="lg"
          fullWidth
          className="mt-4"
          loading={paying}
          // Not while a re-quote is in flight: the figure on this button is
          // about to be replaced, and a payment taken against the old one is
          // the one mistake a checkout cannot make.
          disabled={pricing}
          onClick={onPay}
        >
          {/* The figure on the button itself: what is about to be taken should
              be readable without looking back up at the total, which is the one
              line a buyer checks twice before pressing.

              Two spans rather than one string, so the word and the figure are
              set apart by the button's own gap — the same one it puts between
              an icon and its label — instead of by a single space that runs
              "Pay" into the rupee sign. The accessible name is unaffected: it
              is still read out as one phrase. */}
          <span>Pay</span>
          <span>{formatMoney(price.total)}</span>
        </Button>
      )}
    </section>
  );
};

export default OrderSummary;
